import "@/App.css";
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import {
  Header,
  HeroSection,
  ComparisonSection,
  FeaturesSection,
  ProcessingSection,
  BenchmarkSection,
  UseCasesSection,
  ROISection,
  CTASection,
  Footer,
  IndonesianFocusSection,
  ComplexDataSection,
} from './components/landing';
import AdminLayout from './components/admin/AdminLayout';
import LoginPage from './components/admin/LoginPage';
import DashboardOverview from './components/admin/DashboardOverview';
import LeadsPage from './components/admin/LeadsPage';
import CRMPage from './components/admin/CRMPage';
import AnalyticsPage from './components/admin/AnalyticsPage';
import MetricsPage from './components/admin/MetricsPage';

import ProductPage from './pages/ProductPage';
import ResearchPage from './pages/ResearchPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SecurityPage from './pages/SecurityPage';

const PAGE_META = {
  '/': {
    title: 'Doora: Traceable Enterprise AI Platform | AI Knowledge Layer',
    description: 'Doora is the secure, traceable enterprise AI platform that unifies your knowledge systems - powering auditable copilots, agents, and search with full source attribution. SOC 2 compliant. Deploy in 3 weeks.',
  },
  '/product': {
    title: 'Doora Product: Enterprise AI Features: Traceable, Auditable, Secure AI',
    description: 'Explore Doora\'s enterprise AI features: traceable answers, auditable AI, SOC 2 compliance, knowledge graph, RAG, real-time sync, and 20+ connectors for ERP, CRM, and more.',
  },
  '/research': {
    title: 'Doora Research: Enterprise AI Benchmarks & AI Governance Studies',
    description: 'Read Doora\'s research on enterprise AI performance, AI governance, traceable AI systems, and ROI benchmarks from production deployments across industries.',
  },
  '/security': {
    title: 'Doora Security: SOC 2 Compliant, Secure Enterprise AI Platform',
    description: 'Learn how Doora secures your enterprise AI: SOC 2 compliance, RBAC, on-premise deployment, full audit trails, and data sovereignty. The secure AI platform enterprises trust.',
  },
  '/privacy': {
    title: 'Doora Privacy Policy',
    description: 'Doora privacy policy - how we collect, use, and protect your data on the Doora enterprise AI platform.',
  },
  '/terms': {
    title: 'Doora Terms of Service',
    description: 'Terms of service for using the Doora enterprise AI knowledge platform.',
  },
};

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const meta = PAGE_META[pathname] || PAGE_META['/'];
    document.title = meta.title;
    const descEl = document.querySelector('meta[name="description"]');
    if (descEl) descEl.setAttribute('content', meta.description);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', meta.title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', meta.description);
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', meta.title);
    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) twitterDesc.setAttribute('content', meta.description);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `https://doora.id${pathname === '/' ? '/' : pathname}`);
  }, [pathname]);

  return null;
}

function LandingPage() {
  return (
    <div className="bg-zinc-950 grid-background">
      <HeroSection />
      <ComparisonSection />
      <FeaturesSection />
      <ComplexDataSection />
      <ProcessingSection />
      <IndonesianFocusSection />
      <BenchmarkSection />
      <ROISection />
      <CTASection />
    </div>
  );
}

function PublicLayout({ children }) {
  return (
    <LanguageProvider>
      <div className="App min-h-screen bg-zinc-950 text-zinc-100 flex flex-col">
        <Header />
        <main className="flex-grow">
          {children}
        </main>
        <Footer />
      </div>
    </LanguageProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <Routes>
          <Route path="/" element={<PublicLayout><LandingPage /></PublicLayout>} />
          <Route path="/product" element={<PublicLayout><ProductPage /></PublicLayout>} />
          <Route path="/research" element={<PublicLayout><ResearchPage /></PublicLayout>} />
          <Route path="/terms" element={<PublicLayout><TermsPage /></PublicLayout>} />
          <Route path="/privacy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
          <Route path="/security" element={<PublicLayout><SecurityPage /></PublicLayout>} />
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<DashboardOverview />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="crm" element={<CRMPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="metrics" element={<MetricsPage />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
